import React, { useEffect, useState } from 'react';
import { getErrorMessage, isValidOTP } from '../../utils/helpers';
import { toast } from 'react-toastify';
import ColorfullWhatsAppIcon from '../../assets/icons/ColorfullWhatsAppIcon';

interface VerifyOTPProps {
    displayNumber: string;
    mode?: string;
    onSuccess?: (user: any) => void;
    onBackButtonClick: () => void;
    onResend: () => Promise<{ status: boolean; mode?: string } | void>;
    onVerify: (otp: string) => Promise<any>;
}

const VerifyOTP: React.FC<VerifyOTPProps> = ({
    mode,
    displayNumber,
    onSuccess,
    onBackButtonClick,
    onResend,
    onVerify,
}) => {
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']); // 6-digit OTP

    const [otpMode, setOTPMode] = useState<string>(mode ?? '');
    const [invalid, setInvalid] = useState(false);

    const [resendIn, setResendIn] = useState<number>(0);
    const [verifying, setVerifying] = useState<boolean>(false);

    const [resending, setResending] = useState<boolean>(false);

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        const newOtp = [...otp];
        newOtp[index] = value;

        // Move focus to next input when a digit is entered
        if (value && index < otp.length - 1) {
            const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
            nextInput?.focus();
        }

        setOtp(newOtp);
    };

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        if (resendIn > 0) {
            interval = setInterval(() => {
                setResendIn((prev) => prev - 1);
            }, 1000)
        } else {
            if (interval) {
                clearInterval(interval);
            }
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [resendIn])

    useEffect(() => {
        setResendIn(30);
    }, [])

    const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const previousInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
            previousInput?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('Text').trim();
        if (pastedData.length === otp.length && /^[0-9]+$/.test(pastedData)) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);

            // Focus the last input field
            const lastInput = document.getElementById(`otp-${otp.length - 1}`) as HTMLInputElement;
            lastInput?.focus();
        }
        e.preventDefault(); // Prevent the default paste behavior
    };

    const handleResendCode = async () => {
        if (resendIn || resending) return;

        try {
            setResending(true);
            const res = await onResend();
            if (res?.status) {
                setOTPMode(res.mode!);
                setResendIn(30);
                toast.success("OTP has been resent!");
                setOtp(['', '', '', '', '', ''])
            }
        } catch (error) {
            console.error(error);
        } finally {
            setResending(false);
        }
    };

    const handleVerifyAccount = async () => {
        const otpString = otp.join('');
        if (!otpString || otpString.length !== 6 || verifying) return;
        setInvalid(false);
        try {
            setVerifying(true);
            const data = await onVerify(otpString);
            if (onSuccess)
                onSuccess(data);
        } catch (error: any) {
            setInvalid(true);
            toast.error(getErrorMessage(error));
            console.log(error);
            if(error.response.data?.error_code === 'user_not_found'){
            setTimeout(() => { 
                window.location.href = '/register'
                }, 2000)
            }
        } finally {
            setVerifying(false);
        }
    };

    useEffect(() => {
        if (isValidOTP(otp))
            handleVerifyAccount();
    }, [otp])

    useEffect(() => {
        const otpString = otp.join('');
        if (otpString.length < 6) {
            setInvalid(false);
        }
    }, [otp])

    useEffect(() => {
        const firstOTPField = document.getElementById('otp-0');
        if (firstOTPField) {
            firstOTPField.focus();
        }
    }, [])

    return (
        <div className='d-flex flex-column align-items-center'>
            <h4 className="text-dark w-100 text-center auth-heading">OTP Verification</h4>
            <span className='otp-verification-text'>Enter the verification code we sent via {otpMode === 'whatsapp' ? "WhatsApp" : "SMS"} to
            </span>
            <span className="mobile-email d-flex align-items-center gap-1">
                <ColorfullWhatsAppIcon />
                {displayNumber}
                <span onClick={() => onBackButtonClick()} className='cursor-pointer ms-1'><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.3337 8.75008V10.0001C18.3337 13.9285 18.3337 15.8926 17.1133 17.113C15.8929 18.3334 13.9287 18.3334 10.0003 18.3334C6.07195 18.3334 4.10777 18.3334 2.88738 17.113C1.66699 15.8926 1.66699 13.9285 1.66699 10.0001C1.66699 6.07171 1.66699 4.10752 2.88738 2.88714C4.10777 1.66675 6.07195 1.66675 10.0003 1.66675H11.2503" stroke="var(--primary)" strokeWidth="1.25" strokeLinecap="round" />
                    <path d="M13.877 2.8793L14.4177 2.33862C15.3135 1.44279 16.766 1.44279 17.6618 2.33862C18.5576 3.23445 18.5576 4.68687 17.6618 5.5827L17.1211 6.12338M13.877 2.8793C13.877 2.8793 13.9446 4.02824 14.9584 5.04202C15.9722 6.05579 17.1211 6.12338 17.1211 6.12338M13.877 2.8793L8.90631 7.85003C8.56963 8.1867 8.40129 8.35504 8.25652 8.54065C8.08574 8.75961 7.93932 8.99651 7.81986 9.24718C7.71859 9.45968 7.6433 9.68553 7.49274 10.1372L7.01067 11.5834M17.1211 6.12338L12.1504 11.0941C11.8137 11.4308 11.6454 11.5991 11.4598 11.7439C11.2408 11.9147 11.0039 12.0611 10.7532 12.1806C10.5407 12.2818 10.3149 12.3571 9.86318 12.5077L8.41699 12.9897M8.41699 12.9897L7.48123 13.3017C7.25895 13.3758 7.01388 13.3179 6.84819 13.1522C6.68251 12.9865 6.62466 12.7415 6.69875 12.5192L7.01067 11.5834M8.41699 12.9897L7.01067 11.5834" stroke="var(--primary)" strokeWidth="1.25" />
                </svg>
                </span>
            </span>
            <div className="text-center mx-n3 otp-digits-container">
                {otp.map((digit, index) => (
                    <input
                        autoComplete='off'
                        key={index}
                        id={`otp-${index}`}
                        className={`otp otp-digit mx-1 rounded-sm text-center font-20 font-900 ${invalid ? "invalid" : ''}`}
                        type="tel"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleBackspace(e, index)}
                        onPaste={index === 0 ? handlePaste : undefined} // Only listen to paste on the first input
                    />
                ))}
            </div>
            <div className="resend-otp">
                {resendIn ? (
                    <span>Resend in {resendIn} seconds</span>
                ) : (
                    <span onClick={handleResendCode} className="resend-otp-action">Resend OTP</span>
                )}
            </div>
        </div>
    );
};

export default VerifyOTP;
